import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Callout,
  Card,
  Checkbox,
  Collapse,
  Icon,
  InputGroup,
  NonIdealState,
  NumericInput,
  Spinner,
  Switch,
  Tag,
  TagInput,
  Tooltip,
} from "@blueprintjs/core";
import {
  api,
  type CreditsStatus,
  type PeopleSearchRequest,
  type PeopleSearchResponse,
  type PeopleSearchResultItem,
  type RevealedPerson,
  type RevealRequest,
  type RevealResponse,
  type RevealPhoneStatusResponse,
} from "../lib/api";
import { CreditConfirmDialog, type RevealTarget } from "../components/CreditConfirmDialog";
import { PeopleProfileDrawer } from "../components/PeopleProfileDrawer";
import { showToast } from "../lib/toaster";

// ─────────── Filter option presets ───────────

const SENIORITY_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "founder", label: "Founder" },
  { value: "c_suite", label: "C-Suite" },
  { value: "partner", label: "Partner" },
  { value: "vp", label: "VP" },
  { value: "head", label: "Head" },
  { value: "director", label: "Director" },
  { value: "manager", label: "Manager" },
  { value: "senior", label: "Senior" },
  { value: "entry", label: "Entry" },
  { value: "intern", label: "Intern" },
];

const EMAIL_STATUS_OPTIONS = [
  { value: "verified", label: "Verified", intent: "success" as const },
  { value: "likely_to_engage", label: "Likely to engage", intent: "primary" as const },
  { value: "unverified", label: "Unverified", intent: "none" as const },
  { value: "unavailable", label: "Unavailable", intent: "warning" as const },
];

const HEADCOUNT_OPTIONS = [
  { value: "1,10", label: "1-10" },
  { value: "11,50", label: "11-50" },
  { value: "51,200", label: "51-200" },
  { value: "201,500", label: "201-500" },
  { value: "501,1000", label: "501-1K" },
  { value: "1001,5000", label: "1K-5K" },
  { value: "5001,10000", label: "5K-10K" },
  // Use a finite numeric upper bound to represent "10K+" — the underlying
  // index requires numeric bounds.
  { value: "10001,99999999", label: "10K+" },
];

// ─────────── Local types ───────────

interface RevealState {
  emailLoading: boolean;
  phoneLoading: boolean;
  email?: string | null;
  emailVerified?: boolean;
  phone?: string | null;
  phoneType?: string | null;
  cached?: boolean;
  pending?: boolean;
  pendingSince?: number | null;
  pendingTimer?: number | null;
  cacheKey?: string | null;
  noResult?: { kind: "email" | "phone"; message: string } | null;
  // Full unmasked profile from Apollo's /people/match response. Populated on
  // any successful reveal so the drawer can show the unmasked last name,
  // headline, and any newly-known fields.
  revealedPerson?: RevealedPerson | null;
}

type RevealMap = Map<string, RevealState>;

// ─────────── Helpers ───────────

function resultKey(r: PeopleSearchResultItem): string {
  return r.apollo_person_id || r.linkedin_url || `${r.first_name ?? ""}|${r.last_name ?? ""}|${r.company_name ?? ""}`;
}

function buildRevealRequest(r: PeopleSearchResultItem): RevealRequest {
  return {
    apollo_person_id: r.apollo_person_id ?? undefined,
    linkedin_url: r.linkedin_url ?? undefined,
    first_name: r.first_name ?? undefined,
    last_name: r.last_name && !r.last_name.includes("***") ? r.last_name : undefined,
    domain: r.company_domain ?? undefined,
    organization_name: r.company_name ?? undefined,
  };
}

function locationOf(r: PeopleSearchResultItem | RevealedPerson): string | null {
  const parts = [r.city, r.state, r.country].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

const AVATAR_COLORS = ["#2b95d6", "#634dbf", "#0d8050", "#c87619", "#8f398f", "#1f6b6b", "#96290d", "#5642a6"];
function avatarColor(seed: string) {
  let h = 0;
  for (const c of seed) h = ((h << 5) - h + c.charCodeAt(0)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// Toggle a value in/out of an array.
function toggle<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
}

// Friendly label for the search strategy returned by the backend.
const STRATEGY_LABELS: Record<string, string> = {
  linkedin_url_match: "LinkedIn profile",
  "name+domain": "Name + company domain",
  "name+inferred_domain": "Name + company",
  "name+company_keywords": "Name + company keywords",
  name_only: "Name",
  company_domain_only: "Company domain",
  inferred_company_domain_only: "Company (inferred domain)",
  company_keyword_only: "Company keywords",
  filters_only: "Filters",
  no_match: "No match",
};

// Friendly label for filter-param keys shown in the diagnostics list.
const PARAM_LABELS: Record<string, string> = {
  q: "Keywords",
  q_keywords: "Keywords",
  linkedin_url: "LinkedIn URL",
  organization_domains: "Company domain",
  q_organization_domains_list: "Company domain",
  person_titles: "Job title",
  include_similar_titles: "Include similar titles",
  person_seniorities: "Seniority",
  person_locations: "Person location",
  organization_locations: "Company location",
  contact_email_status: "Email status",
  organization_num_employees_ranges: "Headcount",
  revenue_range_min: "Revenue min",
  revenue_range_max: "Revenue max",
  currently_using_any_of_technology_uids: "Uses any of",
  currently_using_all_of_technology_uids: "Uses all of",
  currently_not_using_any_of_technology_uids: "Excludes tech",
  q_organization_job_titles: "Hiring title",
  organization_job_locations: "Hiring location",
  organization_num_jobs_min: "Postings min",
  organization_num_jobs_max: "Postings max",
  organization_job_posted_at_min: "Posted from",
  organization_job_posted_at_max: "Posted to",
};

function friendlyStrategy(name: string): string {
  return STRATEGY_LABELS[name] || name.replace(/_/g, " ").replace(/\+/g, " + ");
}

function friendlyParam(key: string): string {
  return PARAM_LABELS[key] || key.replace(/_/g, " ");
}

function formatParamValue(v: unknown): string {
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (v === null || v === undefined) return "";
  return String(v);
}

// ─────────── Component ───────────

export function PeopleSearch() {
  // Quick search
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");

  // Person filters
  const [titles, setTitles] = useState<string[]>([]);
  const [includeSimilarTitles, setIncludeSimilarTitles] = useState<boolean>(true);
  const [seniorities, setSeniorities] = useState<string[]>([]);
  const [personLocations, setPersonLocations] = useState<string[]>([]);

  // Email status
  const [emailStatuses, setEmailStatuses] = useState<string[]>([]);

  // Company filters
  const [orgLocations, setOrgLocations] = useState<string[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [headcounts, setHeadcounts] = useState<string[]>([]);
  const [revenueMin, setRevenueMin] = useState<number | "">("");
  const [revenueMax, setRevenueMax] = useState<number | "">("");

  // Technology filters
  const [techAny, setTechAny] = useState<string[]>([]);
  const [techAll, setTechAll] = useState<string[]>([]);
  const [techExclude, setTechExclude] = useState<string[]>([]);

  // Hiring signals
  const [hiringTitles, setHiringTitles] = useState<string[]>([]);
  const [hiringLocations, setHiringLocations] = useState<string[]>([]);
  const [numJobsMin, setNumJobsMin] = useState<number | "">("");
  const [numJobsMax, setNumJobsMax] = useState<number | "">("");
  const [postedMin, setPostedMin] = useState<string>("");
  const [postedMax, setPostedMax] = useState<string>("");

  // Section open/closed
  const [sectionOpen, setSectionOpen] = useState({
    person: true,
    email: false,
    company: true,
    technology: false,
    hiring: false,
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);

  // Search state
  const [searching, setSearching] = useState(false);
  const [response, setResponse] = useState<PeopleSearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [credits, setCredits] = useState<CreditsStatus | null>(null);
  const [reveals, setReveals] = useState<RevealMap>(new Map());

  // Confirm dialog
  const [dialogTarget, setDialogTarget] = useState<{
    person: PeopleSearchResultItem;
    type: "email" | "phone";
  } | null>(null);

  // Profile drawer (opened by clicking a result card).
  const [drawerPerson, setDrawerPerson] = useState<PeopleSearchResultItem | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  // Live phone-poll timers keyed by result-key. Cleared on unmount and on
  // successful delivery / no-data terminal state.
  const phonePollTimersRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    nameInputRef.current?.focus();
    api.get<CreditsStatus>("/people-search/credits").then(setCredits).catch(() => setCredits(null));
    return () => {
      for (const t of phonePollTimersRef.current.values()) window.clearTimeout(t);
      phonePollTimersRef.current.clear();
    };
  }, []);

  const updateReveal = useCallback((key: string, patch: Partial<RevealState>) => {
    setReveals((prev) => {
      const next = new Map(prev);
      const existing = next.get(key) ?? { emailLoading: false, phoneLoading: false };
      next.set(key, { ...existing, ...patch });
      return next;
    });
  }, []);

  // Poll /reveal-phone-status every 6s for up to 5 min. Apollo's docs say
  // the webhook fires "several minutes after a successful API request"
  // (https://docs.apollo.io/docs/retrieve-mobile-phone-numbers-for-contacts).
  const startPhoneStatusPolling = useCallback(
    (key: string, cacheKey: string) => {
      const startedAt = Date.now();
      const MAX_MS = 5 * 60 * 1000; // 5 minutes
      const INTERVAL_MS = 6000;

      const tick = async () => {
        try {
          const status = await api.get<RevealPhoneStatusResponse>(
            `/people-search/reveal-phone-status?cache_key=${encodeURIComponent(cacheKey)}`,
          );
          if (!status.pending) {
            const phone = status.person?.phone ?? null;
            const phoneType = status.person?.phone_type ?? null;
            if (phone) {
              updateReveal(key, {
                pending: false,
                phone,
                phoneType,
                cached: false,
                revealedPerson: status.person ?? null,
              });
              showToast({
                intent: "success",
                icon: "tick-circle",
                message: `Phone revealed: ${phone}`,
                action: { text: "Copy", icon: "clipboard", onClick: () => copyToClipboard(phone, "phone copied") },
                timeout: 7000,
              });
            } else {
              updateReveal(key, {
                pending: false,
                revealedPerson: status.person ?? null,
                noResult: { kind: "phone", message: "No phone on file. No credits charged." },
              });
              showToast({
                intent: "warning",
                icon: "info-sign",
                message: "Apollo could not verify a phone number for this contact. No credits charged.",
                timeout: 6000,
              });
            }
            phonePollTimersRef.current.delete(key);
            return;
          }
        } catch {
          // 404 here is expected briefly between request and DB write — keep polling.
        }
        if (Date.now() - startedAt > MAX_MS) {
          updateReveal(key, {
            pending: false,
            noResult: { kind: "phone", message: "Verification timed out. Please try again later." },
          });
          showToast({
            intent: "warning",
            icon: "time",
            message: "Phone verification timed out. Apollo's webhook didn't deliver within 5 minutes.",
            timeout: 6000,
          });
          phonePollTimersRef.current.delete(key);
          return;
        }
        const t = window.setTimeout(tick, INTERVAL_MS);
        phonePollTimersRef.current.set(key, t);
      };

      // Kick off after a short delay — Apollo docs say "several minutes"
      // before the webhook fires; first-poll is mostly to confirm the row was
      // written. The real wait happens in subsequent ticks.
      const t = window.setTimeout(tick, 4000);
      phonePollTimersRef.current.set(key, t);
    },
    [updateReveal],
  );

  const buildRequest = useCallback(
    (overridePage?: number): PeopleSearchRequest => ({
      name: name.trim() || undefined,
      company: company.trim() || undefined,
      person_titles: titles.length ? titles : undefined,
      include_similar_titles: titles.length && !includeSimilarTitles ? false : undefined,
      person_seniorities: seniorities.length ? seniorities : undefined,
      person_locations: personLocations.length ? personLocations : undefined,
      contact_email_status: emailStatuses.length ? emailStatuses : undefined,
      organization_locations: orgLocations.length ? orgLocations : undefined,
      organization_domains: domains.length ? domains : undefined,
      organization_num_employees_ranges: headcounts.length ? headcounts : undefined,
      revenue_range_min: revenueMin === "" ? undefined : Number(revenueMin),
      revenue_range_max: revenueMax === "" ? undefined : Number(revenueMax),
      currently_using_any_of_technology_uids: techAny.length ? techAny : undefined,
      currently_using_all_of_technology_uids: techAll.length ? techAll : undefined,
      currently_not_using_any_of_technology_uids: techExclude.length ? techExclude : undefined,
      q_organization_job_titles: hiringTitles.length ? hiringTitles : undefined,
      organization_job_locations: hiringLocations.length ? hiringLocations : undefined,
      organization_num_jobs_min: numJobsMin === "" ? undefined : Number(numJobsMin),
      organization_num_jobs_max: numJobsMax === "" ? undefined : Number(numJobsMax),
      organization_job_posted_at_min: postedMin || undefined,
      organization_job_posted_at_max: postedMax || undefined,
      page: overridePage ?? page,
      per_page: perPage,
    }),
    [
      name, company, titles, includeSimilarTitles, seniorities, personLocations,
      emailStatuses, orgLocations, domains, headcounts, revenueMin, revenueMax,
      techAny, techAll, techExclude, hiringTitles, hiringLocations,
      numJobsMin, numJobsMax, postedMin, postedMax, page, perPage,
    ],
  );

  // Count of active filters (for header chip)
  const activeFilterCount = useMemo(() => {
    const tag = (v: unknown) => (Array.isArray(v) ? v.length > 0 : v !== undefined && v !== "" && v !== null);
    return [
      titles, seniorities, personLocations, emailStatuses, orgLocations, domains,
      headcounts, techAny, techAll, techExclude, hiringTitles, hiringLocations,
    ].filter(tag).length
      + (revenueMin !== "" ? 1 : 0) + (revenueMax !== "" ? 1 : 0)
      + (numJobsMin !== "" ? 1 : 0) + (numJobsMax !== "" ? 1 : 0)
      + (postedMin ? 1 : 0) + (postedMax ? 1 : 0)
      + (titles.length && !includeSimilarTitles ? 1 : 0);
  }, [
    titles, includeSimilarTitles, seniorities, personLocations, emailStatuses, orgLocations,
    domains, headcounts, revenueMin, revenueMax, techAny, techAll, techExclude,
    hiringTitles, hiringLocations, numJobsMin, numJobsMax, postedMin, postedMax,
  ]);

  const resetAllFilters = useCallback(() => {
    setTitles([]); setIncludeSimilarTitles(true);
    setSeniorities([]); setPersonLocations([]);
    setEmailStatuses([]);
    setOrgLocations([]); setDomains([]); setHeadcounts([]);
    setRevenueMin(""); setRevenueMax("");
    setTechAny([]); setTechAll([]); setTechExclude([]);
    setHiringTitles([]); setHiringLocations([]);
    setNumJobsMin(""); setNumJobsMax("");
    setPostedMin(""); setPostedMax("");
  }, []);

  const runSearch = useCallback(
    async (overridePage?: number) => {
      const req = buildRequest(overridePage);
      const hasAnyText = req.name || req.company || req.linkedin_url;
      const hasAnyFilter = activeFilterCount > 0;
      if (!hasAnyText && !hasAnyFilter) {
        setError("Enter a name, a company, a LinkedIn URL, or at least one filter.");
        return;
      }
      setError(null);
      setSearching(true);
      setReveals(new Map());
      try {
        const data = await api.post<PeopleSearchResponse>("/people-search/search", req);
        setResponse(data);
        if (overridePage !== undefined) setPage(overridePage);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Search failed";
        setError(msg);
        setResponse(null);
      } finally {
        setSearching(false);
      }
    },
    [buildRequest, activeFilterCount],
  );

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setPage(1);
      runSearch(1);
    }
  };

  const onClickReveal = (person: PeopleSearchResultItem, type: "email" | "phone") => {
    const key = resultKey(person);
    const state = reveals.get(key);
    if (state?.[type === "email" ? "email" : "phone"]) {
      copyToClipboard(state[type === "email" ? "email" : "phone"] as string, `${type} copied`);
      return;
    }
    setDialogTarget({ person, type });
  };

  const performReveal = async () => {
    if (!dialogTarget) return;
    const { person, type } = dialogTarget;
    const key = resultKey(person);
    const reqBody = buildRevealRequest(person);

    if (!reqBody.apollo_person_id && !reqBody.linkedin_url && (!reqBody.first_name || !reqBody.last_name)) {
      showToast({
        intent: "warning",
        icon: "warning-sign",
        message: "Not enough identifiers to reveal this contact.",
      });
      setDialogTarget(null);
      return;
    }

    updateReveal(key, type === "email" ? { emailLoading: true } : { phoneLoading: true });

    try {
      const path = type === "email" ? "/people-search/reveal-email" : "/people-search/reveal-phone";
      const res = await api.post<RevealResponse>(path, reqBody);

      if (!res.success && !res.pending) {
        updateReveal(key, {
          emailLoading: false,
          phoneLoading: false,
          noResult: { kind: type, message: res.message || "No data found. No credits charged." },
        });
        showToast({
          intent: "warning",
          icon: "info-sign",
          message: res.message || `No ${type} found. No credits charged.`,
          timeout: 5000,
        });
        setDialogTarget(null);
        return;
      }

      if (res.pending) {
        updateReveal(key, {
          emailLoading: false,
          phoneLoading: false,
          pending: true,
          pendingSince: Date.now(),
          cacheKey: res.cache_key ?? null,
        });
        showToast({ intent: "primary", icon: "time", message: res.message, timeout: 6000 });
        if (type === "phone" && res.cache_key) {
          startPhoneStatusPolling(key, res.cache_key);
        }
        setDialogTarget(null);
        return;
      }

      const p = res.person;
      const patch: Partial<RevealState> = {
        emailLoading: false,
        phoneLoading: false,
        cached: res.cached,
        revealedPerson: p ?? null,
      };
      if (type === "email" && p?.email) {
        patch.email = p.email;
        patch.emailVerified = p.email_verified;
      }
      if (type === "phone" && p?.phone) {
        patch.phone = p.phone;
        patch.phoneType = p.phone_type;
      }
      updateReveal(key, patch);

      const revealedValue = type === "email" ? p?.email : p?.phone;
      showToast({
        intent: "success",
        icon: "tick-circle",
        message: res.cached
          ? `Already revealed in last 30 days — ${type}: ${revealedValue}`
          : `${type === "email" ? "Email" : "Phone"} revealed: ${revealedValue}`,
        action: revealedValue
          ? {
              text: "Copy",
              icon: "clipboard",
              onClick: () => copyToClipboard(revealedValue, `${type} copied`),
            }
          : undefined,
        timeout: 7000,
      });
    } catch (e) {
      updateReveal(key, { emailLoading: false, phoneLoading: false });
      const msg = e instanceof Error ? e.message : "Reveal failed";
      showToast({ intent: "danger", icon: "error", message: msg, timeout: 6000 });
    } finally {
      setDialogTarget(null);
    }
  };

  const totalPages = response?.total_pages ?? 0;
  const totalEntries = response?.total_entries ?? 0;

  const dialogTargetForUI: RevealTarget | null = useMemo(() => {
    if (!dialogTarget) return null;
    const p = dialogTarget.person;
    return {
      full_name: p.full_name,
      title: p.title,
      company_name: p.company_name,
      photo_url: p.photo_url,
    };
  }, [dialogTarget]);

  const dialogLoading =
    !!dialogTarget &&
    (() => {
      const k = resultKey(dialogTarget.person);
      const s = reveals.get(k);
      return dialogTarget.type === "email" ? !!s?.emailLoading : !!s?.phoneLoading;
    })();

  // ─────────── Render ───────────

  return (
    <div className="people-search-page">
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, margin: 0, fontWeight: 600 }}>People Search</h1>
        <div style={{ color: "#5c7080", fontSize: 13, marginTop: 4 }}>
          Search 270M+ verified contacts. Searching is free — revealing email or phone uses credits.
        </div>
      </header>

      {credits && !credits.configured && (
        <Callout intent="danger" icon="error" title="Contact search is not configured" style={{ marginBottom: 16 }}>
          {credits.message}
        </Callout>
      )}

      {/* Quick search header */}
      <Card elevation={1} style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr auto", gap: 8, alignItems: "stretch" }}>
          <InputGroup
            inputRef={nameInputRef}
            large
            leftIcon="user"
            placeholder='Name (e.g. "Patrick Collison" or LinkedIn URL)'
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleEnter}
            disabled={searching}
          />
          <InputGroup
            large
            leftIcon="office"
            placeholder='Company (e.g. "Stripe" or "stripe.com")'
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            onKeyDown={handleEnter}
            disabled={searching}
          />
          <Button
            intent="primary"
            large
            icon="search"
            loading={searching}
            onClick={() => {
              setPage(1);
              runSearch(1);
            }}
          >
            Search
          </Button>
        </div>
      </Card>

      {/* Two-column layout: filter sidebar + results */}
      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16, alignItems: "flex-start" }}>
        {/* Sidebar */}
        <Card elevation={0} className="ps-filters" style={{ padding: 0, position: "sticky", top: 8 }}>
          <div style={{
            padding: "12px 14px",
            borderBottom: "1px solid #e1e8ed",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Icon icon="filter" size={14} />
              <strong style={{ fontSize: 13 }}>Filters</strong>
              {activeFilterCount > 0 && (
                <Tag minimal intent="primary" round style={{ fontSize: 10 }}>
                  {activeFilterCount}
                </Tag>
              )}
            </div>
            <Button
              minimal
              small
              icon="reset"
              disabled={activeFilterCount === 0 || searching}
              onClick={resetAllFilters}
              title="Clear all filters"
            >
              Reset
            </Button>
          </div>

          {/* Person section */}
          <FilterSection
            title="Person"
            icon="user"
            isOpen={sectionOpen.person}
            onToggle={() => setSectionOpen((s) => ({ ...s, person: !s.person }))}
            badge={[titles, seniorities, personLocations].filter((a) => a.length).length + (titles.length && !includeSimilarTitles ? 1 : 0)}
          >
            <FilterField label="Job titles" hint="Press Enter or comma to add. Any title matches.">
              <TagInput
                values={titles}
                onChange={(v) => setTitles(v as string[])}
                placeholder='e.g. "Head of Engineering"'
                addOnBlur
                fill
                inputProps={{ style: { fontSize: 12 } }}
              />
              {titles.length > 0 && (
                <Switch
                  checked={includeSimilarTitles}
                  onChange={(e) => setIncludeSimilarTitles((e.target as HTMLInputElement).checked)}
                  label="Include similar titles"
                  style={{ marginTop: 6, fontSize: 12 }}
                />
              )}
            </FilterField>

            <FilterField label="Seniority" hint="Pick one or more. Any selected level matches.">
              <ChipPicker
                options={SENIORITY_OPTIONS}
                values={seniorities}
                onToggle={(v) => setSeniorities((a) => toggle(a, v))}
              />
            </FilterField>

            <FilterField label="Location (person)" hint="City, state, or country where the person lives.">
              <TagInput
                values={personLocations}
                onChange={(v) => setPersonLocations(v as string[])}
                placeholder='e.g. "San Francisco", "United States"'
                addOnBlur
                fill
                inputProps={{ style: { fontSize: 12 } }}
              />
            </FilterField>
          </FilterSection>

          {/* Email section */}
          <FilterSection
            title="Email status"
            icon="envelope"
            isOpen={sectionOpen.email}
            onToggle={() => setSectionOpen((s) => ({ ...s, email: !s.email }))}
            badge={emailStatuses.length}
          >
            <FilterField label="Filter by email status" hint="Limit results to contacts whose email matches one of these statuses.">
              <ChipPicker
                options={EMAIL_STATUS_OPTIONS}
                values={emailStatuses}
                onToggle={(v) => setEmailStatuses((a) => toggle(a, v))}
              />
            </FilterField>
          </FilterSection>

          {/* Company section */}
          <FilterSection
            title="Company"
            icon="office"
            isOpen={sectionOpen.company}
            onToggle={() => setSectionOpen((s) => ({ ...s, company: !s.company }))}
            badge={[orgLocations, domains, headcounts].filter((a) => a.length).length + (revenueMin !== "" ? 1 : 0) + (revenueMax !== "" ? 1 : 0)}
          >
            <FilterField label="Company domain(s)" hint="Match contacts whose current or previous employer uses one of these domains.">
              <TagInput
                values={domains}
                onChange={(v) => setDomains(v as string[])}
                placeholder='e.g. "stripe.com"'
                addOnBlur
                fill
                inputProps={{ style: { fontSize: 12 } }}
              />
            </FilterField>

            <FilterField label="Company HQ location" hint="Headquarters location of the employer.">
              <TagInput
                values={orgLocations}
                onChange={(v) => setOrgLocations(v as string[])}
                placeholder='e.g. "San Francisco", "Germany"'
                addOnBlur
                fill
                inputProps={{ style: { fontSize: 12 } }}
              />
            </FilterField>

            <FilterField label="Headcount" hint="Pick one or more employee-count brackets.">
              <ChipPicker
                options={HEADCOUNT_OPTIONS}
                values={headcounts}
                onToggle={(v) => setHeadcounts((a) => toggle(a, v))}
              />
            </FilterField>

            <FilterField label="Revenue (USD / year)" hint="Annual revenue range. Plain numbers, no symbols.">
              <div style={{ display: "flex", gap: 6 }}>
                <NumericInput
                  fill
                  min={0}
                  placeholder="Min (e.g. 1000000)"
                  value={revenueMin}
                  onValueChange={(v) => setRevenueMin(Number.isFinite(v) ? v : "")}
                  buttonPosition="none"
                />
                <NumericInput
                  fill
                  min={0}
                  placeholder="Max (e.g. 100000000)"
                  value={revenueMax}
                  onValueChange={(v) => setRevenueMax(Number.isFinite(v) ? v : "")}
                  buttonPosition="none"
                />
              </div>
            </FilterField>
          </FilterSection>

          {/* Technology section */}
          <FilterSection
            title="Technology"
            icon="code"
            isOpen={sectionOpen.technology}
            onToggle={() => setSectionOpen((s) => ({ ...s, technology: !s.technology }))}
            badge={[techAny, techAll, techExclude].filter((a) => a.length).length}
          >
            <FilterField
              label="Uses any of"
              hint='Tech identifiers (e.g. "salesforce", "google_analytics"). Match if employer uses any.'
            >
              <TagInput
                values={techAny}
                onChange={(v) => setTechAny(v as string[])}
                placeholder='e.g. "salesforce"'
                addOnBlur
                fill
                inputProps={{ style: { fontSize: 12 } }}
              />
            </FilterField>

            <FilterField label="Uses all of" hint="Match only employers whose stack includes every one of these.">
              <TagInput
                values={techAll}
                onChange={(v) => setTechAll(v as string[])}
                placeholder='e.g. "wordpress_org"'
                addOnBlur
                fill
                inputProps={{ style: { fontSize: 12 } }}
              />
            </FilterField>

            <FilterField label="Excludes" hint="Exclude employers using any of these.">
              <TagInput
                values={techExclude}
                onChange={(v) => setTechExclude(v as string[])}
                placeholder='e.g. "shopify"'
                addOnBlur
                fill
                inputProps={{ style: { fontSize: 12 } }}
              />
            </FilterField>
          </FilterSection>

          {/* Hiring signals */}
          <FilterSection
            title="Hiring signals"
            icon="briefcase"
            isOpen={sectionOpen.hiring}
            onToggle={() => setSectionOpen((s) => ({ ...s, hiring: !s.hiring }))}
            badge={[hiringTitles, hiringLocations].filter((a) => a.length).length + (numJobsMin !== "" ? 1 : 0) + (numJobsMax !== "" ? 1 : 0) + (postedMin ? 1 : 0) + (postedMax ? 1 : 0)}
          >
            <FilterField label="Job titles being hired" hint="Roles in the employer's currently-active job postings.">
              <TagInput
                values={hiringTitles}
                onChange={(v) => setHiringTitles(v as string[])}
                placeholder='e.g. "sales engineer"'
                addOnBlur
                fill
                inputProps={{ style: { fontSize: 12 } }}
              />
            </FilterField>

            <FilterField label="Job posting locations" hint="Where the active postings are based.">
              <TagInput
                values={hiringLocations}
                onChange={(v) => setHiringLocations(v as string[])}
                placeholder='e.g. "Atlanta", "Japan"'
                addOnBlur
                fill
                inputProps={{ style: { fontSize: 12 } }}
              />
            </FilterField>

            <FilterField label="Number of open postings" hint="Match employers with this many active postings.">
              <div style={{ display: "flex", gap: 6 }}>
                <NumericInput
                  fill
                  min={0}
                  placeholder="Min"
                  value={numJobsMin}
                  onValueChange={(v) => setNumJobsMin(Number.isFinite(v) ? v : "")}
                  buttonPosition="none"
                />
                <NumericInput
                  fill
                  min={0}
                  placeholder="Max"
                  value={numJobsMax}
                  onValueChange={(v) => setNumJobsMax(Number.isFinite(v) ? v : "")}
                  buttonPosition="none"
                />
              </div>
            </FilterField>

            <FilterField label="Posted between" hint="Filter by when the postings went live.">
              <div style={{ display: "flex", gap: 6 }}>
                <InputGroup
                  type="date"
                  value={postedMin}
                  onChange={(e) => setPostedMin(e.target.value)}
                  fill
                  small
                />
                <InputGroup
                  type="date"
                  value={postedMax}
                  onChange={(e) => setPostedMax(e.target.value)}
                  fill
                  small
                />
              </div>
            </FilterField>
          </FilterSection>

          <div style={{ padding: 12, borderTop: "1px solid #e1e8ed" }}>
            <Button
              fill
              intent="primary"
              icon="search"
              onClick={() => {
                setPage(1);
                runSearch(1);
              }}
              loading={searching}
            >
              Apply filters
            </Button>
          </div>
        </Card>

        {/* Results column */}
        <div>
          {error && (
            <Callout intent="danger" icon="error" style={{ marginBottom: 16 }}>
              {error}
            </Callout>
          )}

          {searching && !response && (
            <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
              <Spinner size={30} />
            </div>
          )}

          {!searching && response && response.results.length === 0 && (
            <NonIdealState
              icon="search"
              title="No people matched"
              description={
                <div style={{ maxWidth: 520 }}>
                  <div>
                    We couldn't find anyone matching this search. We tried{" "}
                    <strong>{response.attempts.length}</strong> approach{response.attempts.length === 1 ? "" : "es"}:
                  </div>
                  <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 12, color: "#5c7080", textAlign: "left" }}>
                    {response.attempts.map((a, i) => (
                      <li key={i} style={{ marginBottom: 4 }}>
                        <strong style={{ color: "#394b59" }}>{friendlyStrategy(a.strategy)}</strong>
                        {Object.keys(a.params).length > 0 && (
                          <span>
                            {" "}using{" "}
                            {Object.entries(a.params)
                              .map(([k, v]) => `${friendlyParam(k)} = ${formatParamValue(v)}`)
                              .join(", ")}
                          </span>
                        )}
                        {" "}— <strong>{a.result_count}</strong> result{a.result_count === 1 ? "" : "s"}
                      </li>
                    ))}
                  </ul>
                  <div style={{ marginTop: 10, fontSize: 13 }}>
                    <strong>Tips:</strong> loosen or clear filters in the sidebar, use the exact company domain,
                    or paste a LinkedIn URL into the Name field for the most precise match.
                  </div>
                </div>
              }
            />
          )}

          {response && response.results.length > 0 && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#5c7080" }}>
                  <span><strong>{response.results.length}</strong> on this page · about <strong>{totalEntries.toLocaleString()}</strong> total</span>
                  {response.strategy && (
                    <Tooltip content="The matching approach that produced these results.">
                      <Tag minimal intent="primary" style={{ fontSize: 10 }}>
                        matched by {friendlyStrategy(response.strategy).toLowerCase()}
                      </Tag>
                    </Tooltip>
                  )}
                </div>
                {totalPages > 1 && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <Button
                      small
                      icon="chevron-left"
                      disabled={page <= 1 || searching}
                      onClick={() => runSearch(page - 1)}
                    >
                      Prev
                    </Button>
                    <span style={{ fontSize: 12, color: "#5c7080" }}>
                      Page {page} of {Math.min(totalPages, 500)}
                    </span>
                    <Button
                      small
                      rightIcon="chevron-right"
                      disabled={page >= totalPages || page >= 500 || searching}
                      onClick={() => runSearch(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {response.results.map((person) => {
                  const key = resultKey(person);
                  const state = reveals.get(key);
                  return (
                    <PersonCard
                      key={key}
                      person={person}
                      state={state}
                      onOpen={() => setDrawerPerson(person)}
                    />
                  );
                })}
              </div>
            </>
          )}

          {!searching && !response && !error && (
            <NonIdealState
              icon="people"
              title="Start your search"
              description={
                <div style={{ maxWidth: 520 }}>
                  <div>Find any of the 270M+ contacts in our directory.</div>
                  <ul style={{ marginTop: 10, paddingLeft: 18, fontSize: 13, color: "#5c7080", textAlign: "left" }}>
                    <li>Enter a <strong>Name</strong> + <strong>Company</strong> for the most precise match.</li>
                    <li>Use a domain (e.g. <code>stripe.com</code>) in the Company field for exact employer filtering.</li>
                    <li>Paste a <strong>LinkedIn URL</strong> into the Name field for a single, exact match.</li>
                    <li>Layer on filters from the sidebar to narrow by title, location, headcount, technology, and more.</li>
                    <li>Searching is free. Revealing an email costs 1 credit; a phone costs 5.</li>
                  </ul>
                </div>
              }
            />
          )}
        </div>
      </div>

      <PeopleProfileDrawer
        open={!!drawerPerson}
        onClose={() => setDrawerPerson(null)}
        person={drawerPerson}
        state={drawerPerson ? reveals.get(resultKey(drawerPerson)) : undefined}
        revealed={drawerPerson ? reveals.get(resultKey(drawerPerson))?.revealedPerson ?? null : null}
        phoneWebhookEnabled={!!credits?.phone_webhook_configured}
        onReveal={(type) => {
          if (drawerPerson) onClickReveal(drawerPerson, type);
        }}
        onCopy={(value, label) => copyToClipboard(value, label)}
      />

      <CreditConfirmDialog
        isOpen={!!dialogTarget}
        target={dialogTargetForUI}
        revealType={dialogTarget?.type ?? "email"}
        estimatedCredits={dialogTarget?.type === "phone" ? (credits?.phone_reveal_credits ?? 5) : (credits?.email_reveal_credits ?? 1)}
        loading={dialogLoading}
        onCancel={() => {
          if (!dialogLoading) setDialogTarget(null);
        }}
        onConfirm={performReveal}
      />
    </div>
  );
}

// ─────────── Sub-components ───────────

function FilterSection({
  title,
  icon,
  isOpen,
  onToggle,
  badge,
  children,
}: {
  title: string;
  icon: React.ComponentProps<typeof Icon>["icon"];
  isOpen: boolean;
  onToggle: () => void;
  badge: number;
  children: React.ReactNode;
}) {
  return (
    <div style={{ borderBottom: "1px solid #e1e8ed" }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: "100%",
          background: "transparent",
          border: "none",
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          fontSize: 13,
          fontWeight: 600,
          color: "#182026",
          textAlign: "left",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon icon={icon} size={13} color="#5c7080" />
          {title}
          {badge > 0 && (
            <Tag minimal intent="primary" round style={{ fontSize: 10 }}>{badge}</Tag>
          )}
        </span>
        <Icon icon={isOpen ? "chevron-down" : "chevron-right"} size={12} color="#5c7080" />
      </button>
      <Collapse isOpen={isOpen}>
        <div style={{ padding: "4px 14px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
          {children}
        </div>
      </Collapse>
    </div>
  );
}

function FilterField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#394b59", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
        {label}
        {hint && (
          <Tooltip content={hint} placement="right">
            <Icon icon="info-sign" size={10} color="#a7b6c2" />
          </Tooltip>
        )}
      </div>
      {children}
    </div>
  );
}

function ChipPicker({
  options,
  values,
  onToggle,
}: {
  options: { value: string; label: string; intent?: "none" | "primary" | "success" | "warning" | "danger" }[];
  values: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {options.map((opt) => {
        const active = values.includes(opt.value);
        return (
          <Tag
            key={opt.value}
            interactive
            minimal={!active}
            intent={active ? (opt.intent ?? "primary") : "none"}
            onClick={() => onToggle(opt.value)}
            style={{
              cursor: "pointer",
              fontSize: 11,
              padding: "2px 8px",
              border: active ? undefined : "1px solid #d8dde2",
            }}
            icon={active ? "tick" : undefined}
          >
            {opt.label}
          </Tag>
        );
      })}
    </div>
  );
}

function copyToClipboard(value: string, message: string) {
  if (!value) return;
  navigator.clipboard.writeText(value).then(
    () => showToast({ intent: "success", icon: "clipboard", message, timeout: 2500 }),
    () => showToast({ intent: "warning", icon: "warning-sign", message: "Copy failed" }),
  );
}

function PersonCard({
  person,
  state,
  onOpen,
}: {
  person: PeopleSearchResultItem;
  state: RevealState | undefined;
  onOpen: () => void;
}) {
  const revealedFirst = state?.revealedPerson?.first_name;
  const revealedLast = state?.revealedPerson?.last_name && !state.revealedPerson.last_name.includes("***")
    ? state.revealedPerson.last_name
    : null;
  const fullName = revealedFirst || revealedLast
    ? [revealedFirst, revealedLast].filter(Boolean).join(" ")
    : person.full_name || `${person.first_name ?? ""} ${person.last_name ?? ""}`.trim() || "Unknown";
  const seedName = revealedFirst || person.first_name || person.full_name || person.last_name || "?";
  const isObfuscated = !revealedLast && !!person.last_name?.includes("***");
  const loc = locationOf(person);

  const emailValue = state?.email ?? null;
  const phoneValue = state?.phone ?? null;
  const pendingPhone = !!state?.pending && !phoneValue;

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <Card
      elevation={0}
      interactive
      className="ps-card"
      style={{ padding: 14, cursor: "pointer" }}
      onClick={onOpen}
      onKeyDown={handleKey}
      role="button"
      tabIndex={0}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        {person.photo_url ? (
          <img
            src={person.photo_url}
            alt=""
            style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: avatarColor(seedName),
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            {seedName.charAt(0).toUpperCase()}
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{fullName}</div>
            {isObfuscated && (
              <Tooltip content="Last name is hidden until you reveal email or phone.">
                <Tag minimal intent="warning" icon="lock" style={{ fontSize: 10 }}>masked</Tag>
              </Tooltip>
            )}
            {person.seniority && (
              <Tag minimal style={{ fontSize: 10, textTransform: "capitalize" }}>
                {person.seniority.replace(/_/g, " ")}
              </Tag>
            )}
          </div>

          <div style={{ fontSize: 13, color: "#394b59", marginTop: 3 }}>
            {[person.title, person.company_name].filter(Boolean).join(" • ") || "—"}
          </div>

          {(loc || person.company_domain || person.company_industry) && (
            <div style={{ fontSize: 12, color: "#5c7080", marginTop: 3, display: "flex", flexWrap: "wrap", gap: 10 }}>
              {loc && <span><Icon icon="geolocation" size={11} /> {loc}</span>}
              {person.company_domain && <span><Icon icon="link" size={11} /> {person.company_domain}</span>}
              {person.company_industry && <span><Icon icon="office" size={11} /> {person.company_industry}</span>}
              {person.company_employee_count != null && <span><Icon icon="people" size={11} /> {person.company_employee_count.toLocaleString()} employees</span>}
            </div>
          )}

          <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
            {/* Already-revealed chips first — instant value indicator. */}
            {emailValue && (
              <Tag minimal intent="success" icon="envelope" style={{ fontSize: 11 }}>
                {emailValue}
              </Tag>
            )}
            {phoneValue && (
              <Tag minimal intent="success" icon="phone" style={{ fontSize: 11 }}>
                {phoneValue}
              </Tag>
            )}
            {pendingPhone && (
              <Tag minimal intent="primary" icon="time" style={{ fontSize: 11 }}>
                Phone verifying…
              </Tag>
            )}

            {/* On-file availability indicators — only when not yet revealed. */}
            {!emailValue && (
              <Tag
                minimal
                icon="envelope"
                intent={person.has_email_on_file ? "primary" : "none"}
                style={{ fontSize: 11, opacity: person.has_email_on_file ? 1 : 0.55 }}
              >
                {person.has_email_on_file ? "Email on file" : "No email on file"}
              </Tag>
            )}
            {!phoneValue && !pendingPhone && (
              <Tag
                minimal
                icon="phone"
                intent={person.has_phone_on_file ? "primary" : "none"}
                style={{ fontSize: 11, opacity: person.has_phone_on_file ? 1 : 0.55 }}
              >
                {person.has_phone_on_file ? "Direct dial on file" : "No direct dial"}
              </Tag>
            )}

            {person.linkedin_url && (
              <a
                href={person.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  fontSize: 12,
                  color: "#137cbd",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  textDecoration: "none",
                }}
              >
                <Icon icon="share" size={11} /> LinkedIn
              </a>
            )}

            <span
              style={{
                fontSize: 11,
                color: "#5c7080",
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                marginLeft: "auto",
              }}
            >
              View profile <Icon icon="chevron-right" size={11} />
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}

// Suppress the unused-import warning for Checkbox (we may use it later for booleans).
void Checkbox;
